import React, { createContext, useState, useEffect, useContext } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getCached = (key, fallback) => {
  try { return localStorage.getItem(key) || fallback; } catch { return fallback; }
};

export const BrandContext = createContext({
  brandName: getCached('brandName', ''),
  primaryColor: getCached('brandColor', '#16a34a'),
  secondaryColor: getCached('brandSecondary', '#059669'),
  accentColor: getCached('brandAccent', '#10b981'),
  logoUrl: getCached('brandLogo', ''),
  fontFamily: getCached('brandFont', 'Inter'),
  refreshBrand: () => {}
});

export const BrandProvider = ({ children }) => {
  const [brandName, setBrandName] = useState(getCached('brandName', ''));
  const [primaryColor, setPrimaryColor] = useState(getCached('brandColor', '#16a34a'));
  const [secondaryColor, setSecondaryColor] = useState(getCached('brandSecondary', '#059669'));
  const [accentColor, setAccentColor] = useState(getCached('brandAccent', '#10b981'));
  const [logoUrl, setLogoUrl] = useState(getCached('brandLogo', ''));
  const [fontFamily, setFontFamily] = useState(getCached('brandFont', 'Inter'));
  const [isLoading, setIsLoading] = useState(true);

  const fetchBrand = async () => {
    try {
      const response = await axios.get(`${API}/brand/settings`);
      const d = response.data;
      const name = d.brand_name || 'Trading Platform';
      const color = d.primary_color || '#16a34a';
      const secondary = d.secondary_color || '#059669';
      const accent = d.accent_color || '#10b981';
      const font = d.font_family || 'Inter';
      const logo = d.has_logo ? `${API}/brand/logo?t=${Date.now()}` : '';
      setBrandName(name);
      setPrimaryColor(color);
      setSecondaryColor(secondary);
      setAccentColor(accent);
      setLogoUrl(logo);
      setFontFamily(font);
      localStorage.setItem('brandName', name);
      localStorage.setItem('brandColor', color);
      localStorage.setItem('brandSecondary', secondary);
      localStorage.setItem('brandAccent', accent);
      localStorage.setItem('brandLogo', logo);
      localStorage.setItem('brandFont', font);
      document.title = `${name}`;
      document.documentElement.style.setProperty('--brand-color', color);
      document.documentElement.style.setProperty('--brand-primary', color);
      document.documentElement.style.setProperty('--brand-secondary', secondary);
      document.documentElement.style.setProperty('--brand-accent', accent);
      document.documentElement.style.setProperty('--brand-font', font);
      // [WL-2026-05-15] White-label: replace browser tab favicon with the
      // tenant's logo on every load so the tab icon never shows the previous
      // owner's image after redeploying for a new customer.
      if (logo) {
        try {
          let fav = document.getElementById('dynamic-favicon');
          if (!fav) {
            fav = document.createElement('link');
            fav.id = 'dynamic-favicon';
            fav.rel = 'icon';
            document.head.appendChild(fav);
          }
          fav.href = logo;
          // Also update apple-touch-icon for iOS home screen
          const appleLinks = document.querySelectorAll('link[rel="apple-touch-icon"]');
          appleLinks.forEach((el) => { el.href = logo; });
        } catch (_) { /* favicon swap is best-effort */ }
      }
    } catch (error) {
      const cached = getCached('brandName', 'Trading Platform');
      document.title = cached;
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const cached = getCached('brandName', '');
    if (cached) document.title = cached;
    const cachedColor = getCached('brandColor', '#16a34a');
    const cachedSecondary = getCached('brandSecondary', '#059669');
    const cachedAccent = getCached('brandAccent', '#10b981');
    document.documentElement.style.setProperty('--brand-color', cachedColor);
    document.documentElement.style.setProperty('--brand-primary', cachedColor);
    document.documentElement.style.setProperty('--brand-secondary', cachedSecondary);
    document.documentElement.style.setProperty('--brand-accent', cachedAccent);
    fetchBrand();
  }, []);

  return (
    <BrandContext.Provider value={{ brandName, primaryColor, secondaryColor, accentColor, logoUrl, fontFamily, refreshBrand: fetchBrand, isLoading }}>
      {children}
    </BrandContext.Provider>
  );
};

export const useBrand = () => {
  const context = useContext(BrandContext);
  if (!context) throw new Error('useBrand must be used within BrandProvider');
  return context;
};
